import { useState, type ReactNode } from "react";
import { ApiError } from "../api/client";
import { useT } from "../settings/context";
import { Icon } from "../ui/Icon";
import { Confirm } from "../ui/Confirm";
import type { Day, Session, Slot } from "./conferences";

/**
 * LE PROGRAMME OÙ L'ON CHOISIT SES CRÉNEAUX : celui du porteur (`routes/Conferences`) et
 * celui d'un accompagnant (`CompanionSteps`), la même liste et la même question.
 *
 * ⚠️ IL N'Y EN AVAIT QU'UN, ET L'AUTRE ÉCRIVAIT AU PREMIER CLIC (bug #519). L'étape
 * accompagnant avait recopié la liste sans sa confirmation : recliquer le créneau
 * d'un accompagnant le désinscrivait aussitôt, en prendre un autre le déplaçait, et sa
 * place pouvait partir avant qu'on la reprenne. Ses refus, eux, tombaient tous sur
 * « Cette étape n'a pas pu être enregistrée ». Un seul composant pour les deux écrans,
 * pour que la règle ne se perde plus en recopiant.
 *
 * ⚠️ L'ÉCRITURE RESTE AU PARENT (`place`) : lui sait pour qui il écrit, ce qu'il journalise
 * et ce qu'il rafraîchit. Elle REJETTE sur un refus du serveur, c'est ce qui permet d'en
 * dire la cause ici.
 *
 * ⚠️ LA DISPOSITION EST CELLE DU PROTOTYPE, classes comprises : `.clist > .cvday >
 * .conf`, une LIGNE PAR SÉANCE groupée par jour et rangée par heure. Voir `programme`.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

/** Ce qu'on s'apprête à faire, quand ça mérite d'être confirmé. */
type Request = {
  slot: Slot;
  /** La personne a déjà un créneau dans cette conférence. */
  kind: "leave" | "change";
};

/**
 * POURQUOI LE SERVEUR A REFUSÉ — et c'est LUI qui le sait.
 *
 * ⚠️ UNE SEULE PHRASE POUR TOUTES LES CAUSES, C'ÉTAIT DEUX ERREURS EN UNE. L'écran
 * conseillait « Réessayez, la place a peut-être été prise entre-temps » quel que soit le
 * refus, en jetant le corps de la réponse. Or le serveur distingue le siège pris à
 * l'instant (`SLOT_UNAVAILABLE`, qui peut se rouvrir si quelqu'un se désinscrit) du
 * maximum de conférences atteint (`CONFERENCE_MAX_REACHED`, qui ne se rouvrira jamais
 * tout seul) — la même nuance que `slotState` documente déjà pour les libellés de ligne.
 * Conseiller de réessayer sur le second, c'est envoyer quelqu'un attendre pour rien.
 *
 * Tout le reste (panne, session expirée, 500) reste une panne : on ne devine pas.
 */
type Refusal = "taken" | "max" | "failure";

function refusalOf(e: unknown): Refusal {
  if (!(e instanceof ApiError) || e.status !== 409) return "failure";
  if (e.message === "CONFERENCE_MAX_REACHED") return "max";
  if (e.message === "SLOT_UNAVAILABLE") return "taken";
  return "failure";
}

export function ConferencePicker({
  days,
  who,
  place,
  busy,
  cfg,
  children,
}: {
  /** Le programme, déjà disposé par `programme`. */
  days: ReadonlyArray<Day>;
  /**
   * POUR QUI L'ON CHOISIT. La question et le refus s'adressent à lui : « Vous retirer de
   * ce créneau ? » posé au porteur qui modifie son accompagnant parlerait de LUI.
   */
  who: "self" | "companion";
  /** Écrit le créneau (le pose, le retire ou le déplace) ; rejette si le serveur refuse. */
  place: (slot: Slot) => Promise<void>;
  /** Un clic ne doit pas partir : écriture en cours, session pas encore prête. */
  busy: boolean;
  /** La famille de réglages où le bloc s'ancre, pour la trappe à CSS. */
  cfg?: string;
  /** Ce qui précède les jours dans la liste : un mot quand il n'y a rien à proposer. */
  children?: ReactNode;
}) {
  const t = useT();
  const companion = who === "companion";
  const [request, setRequest] = useState<Request | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const send = async (slot: Slot) => {
    setRequest(null);
    setRefusal(null);
    try {
      await place(slot);
    } catch (e) {
      setRefusal(refusalOf(e));
    }
  };

  /**
   * ⚠️ ON CONFIRME QUAND ON DÉFAIT, PAS QUAND ON FAIT. Se désinscrire ou changer de
   * créneau rend une place, et sur un programme à quotas elle peut partir dans la
   * seconde : le geste ne se rattrape pas. S'inscrire, lui, se défait d'un clic — une
   * question à ce moment-là ne serait que de la friction, et la friction apprend à
   * cliquer « Oui » sans lire.
   */
  const click = ({ conference, slot, open }: Session) => {
    // `aria-disabled` n'empêche pas le clic — c'est tout l'intérêt : la ligne reste
    // atteignable et lisible. C'est donc ici qu'on refuse.
    if (!open || busy) return;
    if (slot.selected) return setRequest({ slot, kind: "leave" });
    if (conference.selected) return setRequest({ slot, kind: "change" });
    void send(slot);
  };

  return (
    <>
      {refusal && (
        <p className="loginerr" role="alert">
          {refusal === "taken" &&
            t(
              "SLOT_JUST_TAKEN",
              "Ce créneau vient d'être pris. La liste est à jour : choisissez-en un autre.",
            )}
          {refusal === "max" &&
            (companion
              ? t(
                  "COMPANION_CONFERENCES_MAX_REACHED",
                  "Cette personne a atteint le maximum de conférences. Retirez-en une pour en choisir une autre.",
                )
              : t(
                  "CONFERENCES_MAX_REACHED",
                  "Vous avez atteint le maximum de conférences. Retirez-en une pour en choisir une autre.",
                ))}
          {refusal === "failure" &&
            (companion
              ? t(
                  "COMPANION_SLOT_NOT_SAVED",
                  "Son créneau n'a pas pu être enregistré. Réessayez, et prévenez l'organisateur si cela persiste.",
                )
              : t(
                  "SLOT_NOT_SAVED",
                  "Votre créneau n'a pas pu être enregistré. Réessayez, et prévenez l'organisateur si cela persiste.",
                ))}
        </p>
      )}

      <div className="clist" data-cfg={cfg}>
        {children}
        {days.map((day) => (
          <div className="cvday" key={day.day}>
            {/* Le jour, et ce qu'on y a retenu — le compte à droite, comme le
                prototype. Il répond à la question qu'on se pose en parcourant un
                programme : « ai-je quelque chose ce jour-là ? »

                ⚠️ LE PLURIEL EST UNE CLÉ À PART, pas un « s » recollé : toutes les
                langues ne l'accordent pas au même seuil, ni de la même façon. */}
            <div className="cvday__h">
              <b>{day.day}</b>
              <span>
                {day.selected === 0
                  ? t("NONE_SELECTED", "Aucune retenue")
                  : day.selected === 1
                    ? t("N_SELECTED_ONE", "$1 retenue", [day.selected])
                    : t("N_SELECTED_MANY", "$1 retenues", [day.selected])}
              </span>
            </div>

            {day.sessions.map((session) => (
              <button
                type="button"
                key={`${session.conference.id}-${session.slot.id}`}
                className={`conf${session.slot.selected ? " on" : ""}${
                  session.open ? "" : " off"
                }`}
                aria-pressed={session.slot.selected}
                /**
                 * ⚠️ `aria-disabled` ET NON `disabled`, alors que la ligne ne répond
                 * pas au clic. Un bouton désactivé est sauté par la tabulation et
                 * souvent tu pour un lecteur d'écran — or c'est précisément ICI que se
                 * trouve la RAISON (« Complet », « Chevauche une conférence que vous
                 * avez retenue »). La rendre inatteignable au clavier retirerait
                 * l'information à ceux qui en ont le plus besoin. Même arbitrage que
                 * pour un champ verrouillé, voir `funnel/Champ.tsx`.
                 */
                aria-disabled={!session.open || busy}
                onClick={() => click(session)}
              >
                {/* ⚠️ UN `span`, LÀ OÙ LE PROTOTYPE MET UN `div` — le seul écart, et il
                    est obligatoire : un bouton n'accepte que du contenu de phrasé, et
                    un `div` dedans fait défaire l'imbrication au navigateur. La classe
                    est la même, donc le CSS aussi (`display: flex` vaut sur un span).
                    Une maquette n'a pas ce souci ; un site en production, si. */}
                {/* ⚠️ UN ROND, PARCE QU'UNE CONFÉRENCE NE RETIENT QU'UN CRÉNEAU — la
                    règle du tunnel (voir `funnel.css`). C'était le dernier écran de
                    choix sans marqueur : l'état ne se lisait qu'à la bordure, donc au
                    seul contraste, et sur un programme d'une vingtaine de lignes on
                    devait le chercher.

                    ⚠️ IL EST DANS L'EN-TÊTE DE LA LIGNE, avec l'heure et le titre, et
                    non devant toute la carte : une séance porte deux lignes de plus
                    (son détail, son état), et un marqueur centré sur cette hauteur
                    aurait flotté au milieu du vide. */}
                <span className="conf__hd">
                  <span className="opt__r conf__m" aria-hidden="true">
                    {session.slot.selected && <Icon name="check" />}
                  </span>
                  <span className="conf__h">{session.hours}</span>
                  <b>{session.conference.title}</b>
                </span>
                {session.detail && <small>{session.detail}</small>}
                {session.state && <small className="conf__e">{session.state}</small>}
              </button>
            ))}
          </div>
        ))}
      </div>

      <Confirm
        open={request !== null}
        title={
          request?.kind === "leave"
            ? companion
              ? t("COMPANION_LEAVE_SLOT_ASK", "La retirer de ce créneau ?")
              : t("LEAVE_THIS_SLOT_ASK", "Vous retirer de ce créneau ?")
            : t("CHANGE_SLOT_ASK", "Changer de créneau ?")
        }
        message={
          request?.kind === "leave"
            ? companion
              ? t(
                  "COMPANION_LEAVE_SLOT_WARNING",
                  "Sa place sera rendue, et quelqu'un d'autre pourra la prendre. Elle ne la retrouvera pas forcément.",
                )
              : t(
                  "LEAVE_THIS_SLOT_WARNING",
                  "Votre place sera rendue, et quelqu'un d'autre pourra la prendre. Vous ne la retrouverez pas forcément.",
                )
            : companion
              ? t(
                  "COMPANION_CHANGE_SLOT_WARNING",
                  "Sa place actuelle sera rendue au profit de celle-ci. Sur un programme complet, le retour n'est pas garanti.",
                )
              : t(
                  "CHANGE_SLOT_WARNING",
                  "Votre place actuelle sera rendue au profit de celle-ci. Sur un programme complet, le retour n'est pas garanti.",
                )
        }
        confirmLabel={
          request?.kind === "leave"
            ? companion
              ? t("COMPANION_LEAVE_THE_SLOT", "La retirer")
              : t("LEAVE_THE_SLOT", "Me retirer")
            : t("CHANGE_SLOT", "Changer de créneau")
        }
        onConfirm={() => request && void send(request.slot)}
        onCancel={() => setRequest(null)}
        /* Le geste rend une place qu'on ne récupère pas forcément : c'est le bouton
           qui NE fait rien qui doit être le plus facile à trouver. */
        emphasis="cancel"
      />
    </>
  );
}
